import { Component, OnInit } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import { VehicleBrand } from '../shared/models/vehicle-brand';
import { LegalEntity } from '../shared/models/legal-entity';
import { Country } from '../shared/models/country';
import { environment } from '../../environments/environment';
import { DsiService } from './dsi.service';
import { DsiInfo } from '../shared/models/dsi-info';
import { Dealer } from '../shared/models/dealer';

interface CompanyCard {
  dealerNumber: string;
  title: string;
  description: string;
  icon: string;
  iconSize: string;
  lastChanged: string;
}

interface CompanyMeta {
  description: string;
  icon: string;
  lastChanged: string;
}

const ICON_SIZES: Record<string, string> = {
  car: 'h-10 w-10',
  store: 'h-12 w-12',
};
const DEFAULT_ICON_SIZE = 'h-8 w-8';

const COMPANY_META: Record<string, CompanyMeta> = {
  tdg:   { icon: 'users', description: 'Gemeinsame Datenschutzinformation für die genannten Unternehmen.', lastChanged: '5.10.2026' },
  tkg:   { icon: 'bank', description: 'Finanzierungs- und Leasingprodukte.', lastChanged: '5.10.2026' },
  kdg: { icon: 'car', description: 'Mobilitäts-, Leasing- und Abo-Services.', lastChanged: '5.10.2026' },
  tim:   { icon: 'shield', description: 'Toyota KFZ-Versicherungen.', lastChanged: '5.10.2026' },
  ande:  { icon: 'umbrella', description: 'Risikoträger der Toyota Versicherung.', lastChanged: '5.10.2026' },
};

const COMPANY_ORDER = ['tdg', 'tkg', 'kdg', 'tim', 'ande'];

@Component({
  selector: 'toycc-dsi-overview',
  templateUrl: './dsi-overview.component.html',
  standalone: false,
})
export class DsiOverviewComponent implements OnInit {
  brand: VehicleBrand;
  dealerNumber: string;
  legalEntity = LegalEntity.ToyotaGermany;
  requestedDealer: Dealer = null;
  campusCompanies: Dealer[];
  companyCards: CompanyCard[];
  dsiVersion: string;

  constructor(
    private route: ActivatedRoute,
    private dsiService: DsiService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.setBrand(params['brand']);
      if (params['dealerNumber'] && params['dealerNumber'].toLowerCase() !== 'toyota') {
        this.dealerNumber = params['dealerNumber'];
      }
    });

    this.dsiService.getDsiOverviewInfos(this.dealerNumber).subscribe((dsiInfo: DsiInfo) => {
      this.requestedDealer = dsiInfo.dealer;
      this.campusCompanies = dsiInfo.campusCompanies;

      const allCompanies: Dealer[] = [...(this.campusCompanies ?? [])];
      if (this.requestedDealer) {
        allCompanies.push(this.requestedDealer);
      }

      this.companyCards = allCompanies
        .sort((a, b) => this.orderIndex(a) - this.orderIndex(b))
        .map(c => this.toCard(c));
    });

    this.dsiVersion = this.legalEntity.isAustria() ? 'A-2026-1' : 'E-2026-2';
  }

  private setBrand(brand: string) {
    this.brand = new VehicleBrand(brand);
    this.legalEntity = LegalEntity.from(VehicleBrand.from(brand), Country.from(environment.country));
  }

  private toCard(company: Dealer): CompanyCard {
    const meta = COMPANY_META[company.dealerNumber];
    const icon = meta?.icon ?? 'store';

    return {
      dealerNumber: company.dealerNumber,
      title: this.buildTitle(company),
      description: meta?.description ?? `Ihr ${this.brand.name} Vertragspartner.`,
      icon,
      iconSize: ICON_SIZES[icon] ?? DEFAULT_ICON_SIZE,
      lastChanged: meta?.lastChanged ?? null,
    };
  }

  private orderIndex(company: Dealer): number {
    const idx = COMPANY_ORDER.indexOf(company.dealerNumber);
    return idx === -1 ? COMPANY_ORDER.length : idx;
  }

  private buildTitle(company: Dealer): string {
    return company.dealerNumber === 'tdg' && this.requestedDealer
      ? `${company.name} und ${this.requestedDealer.name}`
      : company.name;
  }

  public getDsiDocumentHtml(card: CompanyCard) {
    if (this.requestedDealer && card.dealerNumber === this.requestedDealer.dealerNumber) {
      window.open(this.requestedDealer.urlGeneralDpWithHost, '_blank');
    } else if (this.requestedDealer && card.dealerNumber === 'tdg') {
      this.dsiService.getDsiDocumentHtml(
        this.brand.name.toLowerCase(),
        this.dsiVersion,
        this.requestedDealer.dealerNumber
      );
    } else {
      this.dsiService.getDsiDocumentHtml(
        this.brand.name.toLowerCase(),
        this.dsiVersion,
        card.dealerNumber
      );
    }
  }

  public getDsiDocumentPdf(card: CompanyCard) {
    if (this.requestedDealer && card.dealerNumber === 'tdg') {
      this.dsiService.getDsiDocumentPdf(
        this.brand.name.toLowerCase(),
        this.dsiVersion,
        this.requestedDealer.dealerNumber
      );
    } else {
      this.dsiService.getDsiDocumentPdf(
        this.brand.name.toLowerCase(),
        this.dsiVersion,
        card.dealerNumber
      );
    }
  }
}
